"use client";

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { useAuth } from "@/features/auth/contexts/AuthContext";
import { useCustomerMarketplace as useMarketplace } from "@/features/shop/customer/customer-shop.queries";
import {
  useAddToCustomerCart,
  useSyncLocalCustomerCart,
} from "@/features/shop/customer/customer-shop.mutations";
import type { CustomerProduct } from "@/features/shop/types/customer-shop";
import { CategoriesSection } from "@/features/public-site/components/CategoriesSection";
import { FeaturedProductsSection } from "@/features/public-site/components/FeaturedProductsSection";
import { HomeHeroSection } from "@/features/public-site/components/HomeHeroSection";
import { PopularStoresSection } from "@/features/public-site/components/PopularStoresSection";
import { StartSellingSection } from "@/features/public-site/components/StartSellingSection";

export default function HomePage() {
  const { userAccount, loading } = useAuth();
  const [searchValue, setSearchValue] = useState("");
  const addToCart = useAddToCustomerCart();
  const { mutate: syncLocalCart } = useSyncLocalCustomerCart();

  useEffect(() => {
    if (!loading && userAccount?.role === "customer") {
      syncLocalCart();
    }
  }, [loading, syncLocalCart, userAccount?.role]);

  const marketplace = useMarketplace({
    search: searchValue.trim() || undefined,
  });

  const products = useMemo(
    () => marketplace.data?.data?.products ?? [],
    [marketplace.data?.data?.products],
  );
  const shops = useMemo(
    () => marketplace.data?.data?.shops ?? [],
    [marketplace.data?.data?.shops],
  );

  const featuredProducts = useMemo(() => products.slice(0, 6), [products]);
  const featuredShops = useMemo(() => shops.slice(0, 3), [shops]);
  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    products.forEach((product) => {
      counts.set(product.category, (counts.get(product.category) ?? 0) + 1);
    });
    return Array.from(counts.entries()).map(([category, count]) => ({
      category,
      count,
    }));
  }, [products]);

  async function handleAddToCart(product: CustomerProduct) {
    try {
      const shopId =
        typeof product.shopId === "object" &&
        product.shopId &&
        "_id" in product.shopId
          ? product.shopId._id
          : String(product.shopId);

      await addToCart.mutateAsync({
        shopId,
        productId: product._id,
        quantity: 1,
      });

      toast.success(`${product.name} added to cart.`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to add item to cart",
      );
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-both">
          <HomeHeroSection
            featuredProducts={featuredProducts}
            products={products}
          />
        </div>
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100 fill-mode-both">
          <FeaturedProductsSection
            products={featuredProducts}
            onAddToCart={handleAddToCart}
            isPending={addToCart.isPending}
            isLoading={marketplace.isLoading}
          />
        </div>
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-150 fill-mode-both">
          <CategoriesSection
            categoryCounts={categoryCounts}
            isLoading={marketplace.isLoading}
          />
        </div>
        <section className="mt-12 grid animate-in gap-6 fade-in slide-in-from-bottom-4 duration-500 delay-200 fill-mode-both lg:grid-cols-[0.95fr_1.05fr]">
          <StartSellingSection />
          <PopularStoresSection
            products={products}
            shops={featuredShops}
            isLoading={marketplace.isLoading}
          />
        </section>
      </main>
    </div>
  );
}
